import type { CatalogueDesign } from '@/api/masters';
import { DesignPhoto } from '@/components/DesignPhoto';
import { StatusBadge } from '@/components/StatusBadge';
import { cardAvailability, designCard } from '@core/bookings/catalogue';
import { formatAmount } from '@core/format/money';

/**
 * One design on the catalogue: its photograph first, the basics under it — the proposal's card.
 *
 * <p>
 * Everything it says comes from the core's `designCard`, so the phone's card cannot word a design
 * differently. Availability is a word on the photograph, never colour alone: "4 of 6 free" when it
 * can go, "Back 24 Dec" when none can — amber, the needs-attention colour — and never another
 * customer's name, since a counter screen is often turned to face the customer.
 * </p>
 */
export function DesignCard({
  design,
  days,
  currency,
  selected,
  onOpen,
  footer,
}: {
  design: CatalogueDesign;
  days: number;
  currency: string;
  selected: boolean;
  onOpen: () => void;
  /** Under the card, outside the part that opens the design — a new booking's size buttons. */
  footer?: React.ReactNode;
}) {
  const card = designCard(design, days);
  const availability = cardAvailability(card);

  const frame = `flex flex-col overflow-hidden rounded-lg border bg-white text-start transition-shadow hover:shadow-md ${
    selected ? 'border-primary ring-primary ring-1' : 'border-border-color'
  }`;

  const body = (
    <button
      type="button"
      onClick={onOpen}
      aria-pressed={selected}
      className={footer ? 'flex cursor-pointer flex-col text-start' : `cursor-pointer ${frame}`}
    >
      <DesignPhoto url={card.imageUrl} alt={card.name} categoryPath={design.categoryPath}>
        <span className="absolute start-2 top-2">
          <StatusBadge tone={availability.tone}>{availability.label}</StatusBadge>
        </span>
      </DesignPhoto>

      <span className="flex flex-col gap-0.5 px-3 pt-2 pb-3">
        <span className="truncate text-[length:var(--scale-list-name)] font-semibold text-gray-900">
          {card.name}
        </span>
        <span className="text-default truncate text-[length:var(--scale-list-meta)]">
          {[card.sizes, card.looks].filter((part) => part !== '').join(' · ') || card.code}
        </span>
        <span className="mt-1 text-[length:var(--scale-list-name)]">
          {card.price === null ? (
            // Unfinished setup, not a freebie — nothing prices this design for these days.
            <StatusBadge tone="warning">No rate</StatusBadge>
          ) : (
            <>
              {card.priceVaries && (
                <span className="text-default me-1 text-[length:var(--scale-list-meta)]">from</span>
              )}
              <span className="font-mono-nums font-bold text-gray-900">
                {currency}
                {formatAmount(card.price)}
              </span>
              <span className="text-default ms-1 text-[length:var(--scale-list-meta)]">
                · {days} day{days === 1 ? '' : 's'}
              </span>
            </>
          )}
        </span>
      </span>
    </button>
  );

  return footer ? (
    <div className={frame}>
      {body}
      <div className="px-3 pb-3">{footer}</div>
    </div>
  ) : (
    body
  );
}
